import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActionArea,
  Skeleton,
  Alert,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import { useSelectedSchool } from '../contexts/SelectedSchoolContext';

function formatAddress(addr: { street?: string; city?: string; state?: string; zipCode?: string; country?: string } | undefined): string {
  if (!addr) return '';
  const parts = [addr.street, addr.city, addr.state, addr.zipCode, addr.country].filter(Boolean);
  return parts.join(', ') || '';
}

export default function SelectSchool() {
  const navigate = useNavigate();
  const { schools, schoolsLoading, selectedSchoolId, setSelectedSchoolId } = useSelectedSchool();

  useEffect(() => {
    if (schoolsLoading) return;
    if (schools.length === 0) return;
    if (schools.length === 1) {
      setSelectedSchoolId(schools[0]._id);
      navigate('/', { replace: true });
    } else if (selectedSchoolId && schools.some((s) => s._id === selectedSchoolId)) {
      navigate('/', { replace: true });
    }
  }, [schools, schoolsLoading, selectedSchoolId, setSelectedSchoolId, navigate]);

  const handleSelect = (schoolId: string) => {
    setSelectedSchoolId(schoolId);
    navigate('/', { replace: true });
  };

  if (schoolsLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ width: '100%', maxWidth: 480 }}>
          <Skeleton variant="text" width="80%" height={40} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
        </Box>
      </Box>
    );
  }

  if (schools.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, bgcolor: 'grey.50' }}>
        <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
          <SchoolIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No schools linked</Typography>
          <Typography variant="body2" color="text.secondary">
            You don&apos;t have any students linked yet. Use the link from your school or add a student by ID from the app after signing in.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (schools.length === 1) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4, px: 2 }}>
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>
        <Typography variant="h5" component="h1" fontWeight={700} sx={{ mb: 1 }}>
          Select school
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          You are registered with multiple schools. Select one to proceed.
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {schools.map((school) => (
            <Card key={school._id} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardActionArea onClick={() => handleSelect(school._id)}>
                <CardContent sx={{ py: 2, px: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <SchoolIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {school.name || 'School'}
                      </Typography>
                      {school.address && formatAddress(school.address) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <LocationOnIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {formatAddress(school.address)}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <PeopleIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {school.students.length} registered student{school.students.length !== 1 ? 's' : ''}:{' '}
                          {school.students.map((st) => `${st.name} (${st.class ?? ''}${st.section ? ' ' + st.section : ''})`).join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
